// utilities
"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, /**
 * Compares the given settings with the current image information.
 *
 * @param {Object} [settings={}] - An object of settings to compare. Defaults to an empty object.
 * @param {Object} [imageInfo={}] - An object of image information to compare against settings. Defaults to an empty object.
 * @param {number} [frameIndex=0] - The index of the frame to be checked. If the image was captured as a video, this relates to specific keys in imageInfo associated with a specific frame of an is2. Defaults to 0.
 *
 * @returns {Object} - An object containing two properties: 'nextAttrs' and 'changedKeys'. 'nextAttrs' is an object of updated attributes and 'changedKeys' is an array of keys that have been updated.
 *
 * The function starts by initializing 'nextAttrs' as an empty object and 'changedKeys' as an empty array. If no image information is provided or the image info object has no keys, the function returns these two empty variables.
 *
 * The function then checks if there are any updates in various attributes ('basicPresentationFlags', 'irPaletteSetup', 'info', 'irviewer', 'markers', 'textnotes', 'photonotes') by comparing the settings and image info. These checks are performed using corresponding check functions for each attribute.
 *
 * The keys of the updated attributes are then added to the 'changedKeys' array and the updated attributes are added to 'nextAttrs'.
 *
 * Finally, the function returns an object containing 'nextAttrs' and 'changedKeys'.
 */ "default", {
    enumerable: true,
    get: function() {
        return checkImageInfo;
    }
});
const _ImageInfo = /*#__PURE__*/ _interop_require_default(require("../ImageInfo"));
const _arePropsEqual = /*#__PURE__*/ _interop_require_default(require("./arePropsEqual.js"));
const _editor = require("../../../utilities/editor");
const _enums = require("../enums");
const _utilities = require("../../../utilities");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
const hasKeys = (object)=>Object.keys(object).length !== 0;
function getFlatKeys(obj) {
    return Object.keys(obj).reduce((acc, key)=>{
        return acc.concat(Object.keys(obj[key]));
    }, []);
}
function checkBasicPresentationFlags(settings = {}, imageInfo = {}, frameIndex = 0) {
    const KEY_INFO_DATA = _enums.ImageInfoKeys.THERMAL_IMAGE_INFO_DATA;
    const KEY_ALPHA_LEVEL = _enums.ValidSettings.ALPHA_LEVEL;
    const KEY_PIP_MODE = _enums.ValidSettings.PIP_MODE;
    const keys = [
        KEY_ALPHA_LEVEL,
        KEY_PIP_MODE
    ];
    const info = _ImageInfo.default.getByKey(KEY_INFO_DATA, imageInfo, frameIndex);
    const basicPresentationFlags = info === null || info === void 0 ? void 0 : info.basicPresentationFlags;
    const nextProps = {};
    keys.forEach((key)=>{
        if (key in settings) {
            nextProps[key] = settings[key];
            if (key === KEY_PIP_MODE) {
                nextProps.pipMode = (0, _editor.boolToPip)(nextProps.pipMode);
            }
            if (key === KEY_ALPHA_LEVEL) {
                nextProps.irAlphaLevel = (0, _editor.normalizedToFusion)(nextProps.irAlphaLevel);
            }
        }
    });
    // IMPORTANT - prevents re-renders
    const updatedProps = {};
    for(const key in nextProps){
        const value = nextProps[key];
        const isEqual = (0, _arePropsEqual.default)(basicPresentationFlags[key], value);
        if (isEqual === false) updatedProps[key] = value;
    }
    return updatedProps;
}
function checkIRPaletteSetup(settings = {}, imageInfo = {}, frameIndex = 0) {
    const KEY_INFO_DATA = _enums.ImageInfoKeys.THERMAL_IMAGE_INFO_DATA;
    const KEY_IR_ORIENTATION = _enums.ValidSettings.IR_ORIENTATION;
    const KEY_PALETTE_SCHEME = _enums.ValidSettings.PALETTE_SCHEME;
    const KEY_PALETTE_SCALE_MODE = _enums.ValidSettings.PALETTE_SCALE_MODE;
    const KEY_PALETTE_RANGE_MIN = _enums.ValidSettings.PALETTE_RANGE_MIN;
    const KEY_PALETTE_RANGE_MAX = _enums.ValidSettings.PALETTE_RANGE_MAX;
    const KEY_BACKGROUND_TEMP = _enums.ValidSettings.BACKGROUND_TEMP;
    const keys = [
        KEY_IR_ORIENTATION,
        KEY_PALETTE_SCHEME,
        KEY_PALETTE_SCALE_MODE,
        KEY_PALETTE_RANGE_MIN,
        KEY_PALETTE_RANGE_MAX,
        KEY_BACKGROUND_TEMP
    ];
    const nextProps = {};
    keys.forEach((key)=>{
        if (key in settings) {
            nextProps[key] = settings[key];
        }
    });
    const info = _ImageInfo.default.getByKey(KEY_INFO_DATA, imageInfo, frameIndex);
    const irPaletteSetup = info === null || info === void 0 ? void 0 : info.irPaletteSetup;
    if (KEY_IR_ORIENTATION in nextProps) {
        nextProps.irOrientation = (0, _editor.rotationToIndex)(nextProps.irOrientation);
    }
    const updatedProps = {};
    for(const key in nextProps){
        const value = nextProps[key];
        const isEqual = (0, _arePropsEqual.default)(irPaletteSetup[key], value);
        if (isEqual === false) updatedProps[key] = value;
    }
    return updatedProps;
}
function checkMARKERS(settings = {}, imageInfo = {}) {
    const KEY_MARKERS = _enums.ImageInfoKeys.MARKERS;
    const KEY_MARKERS_STANDARD = _enums.ValidSettings.MARKERS_STANDARD;
    const keys = [
        KEY_MARKERS_STANDARD
    ];
    const nextProps = {};
    keys.forEach((key)=>{
        if (key in settings) {
            nextProps[key] = settings[key];
        }
    });
    const updatedProps = {};
    if (KEY_MARKERS_STANDARD in nextProps) {
        // update visibility of standard markers
        const { [KEY_MARKERS]: MARKERS = {} } = imageInfo;
        const { STANDARD = {} } = MARKERS;
        const { standardMarkers = [] } = nextProps;
        const prevProp = (0, _utilities.getVisibleStandardMarkers)(MARKERS);
        // changed props
        const addedProps = standardMarkers.filter((value)=>!prevProp.includes(value));
        const removedProps = prevProp.filter((value)=>!standardMarkers.includes(value));
        const getMarkerEnabledFlag = (marker)=>{
            var _STANDARD_marker_characteristics, _STANDARD_marker;
            var _STANDARD_marker_characteristics_markerEnabledFlag;
            return (_STANDARD_marker_characteristics_markerEnabledFlag = (_STANDARD_marker = STANDARD[marker]) === null || _STANDARD_marker === void 0 ? void 0 : (_STANDARD_marker_characteristics = _STANDARD_marker.characteristics) === null || _STANDARD_marker_characteristics === void 0 ? void 0 : _STANDARD_marker_characteristics.markerEnabledFlag) !== null && _STANDARD_marker_characteristics_markerEnabledFlag !== void 0 ? _STANDARD_marker_characteristics_markerEnabledFlag : true;
        };
        // update standard marker object
        addedProps.forEach((marker)=>{
            const markerEnabledFlag = getMarkerEnabledFlag(marker);
            if (markerEnabledFlag === false) {
                updatedProps[marker] = STANDARD[marker];
                STANDARD[marker].characteristics.markerEnabledFlag = true;
            }
        });
        removedProps.forEach((marker)=>{
            const markerEnabledFlag = getMarkerEnabledFlag(marker);
            if (markerEnabledFlag === true) {
                updatedProps[marker] = STANDARD[marker];
                STANDARD[marker].characteristics.markerEnabledFlag = false;
            }
        });
    }
    return updatedProps;
}
function checkPHOTONOTES(settings = {}, imageInfo = {}) {
    const KEY_PHOTONOTES = _enums.ImageInfoKeys.PHOTONOTES;
    const KEY_OCR_TEXT = _enums.ValidSettings.OCR_TEXT;
    var _imageInfo_KEY_PHOTONOTES;
    let prevPhotonotes = (_imageInfo_KEY_PHOTONOTES = imageInfo[KEY_PHOTONOTES]) !== null && _imageInfo_KEY_PHOTONOTES !== void 0 ? _imageInfo_KEY_PHOTONOTES : [];
    if (!Array.isArray(imageInfo[KEY_PHOTONOTES])) prevPhotonotes = [];
    let { [KEY_OCR_TEXT]: nextOCRTEXT = [] } = settings;
    // cast as array
    nextOCRTEXT = Array.isArray(nextOCRTEXT) ? nextOCRTEXT : [
        nextOCRTEXT
    ];
    if (prevPhotonotes.length < 1 || nextOCRTEXT.length < 1) return {};
    let prevOCRText = [];
    prevPhotonotes.forEach((photonote)=>{
        var _photonote_KEY_OCR_TEXT;
        const ocrText = (_photonote_KEY_OCR_TEXT = photonote[KEY_OCR_TEXT]) !== null && _photonote_KEY_OCR_TEXT !== void 0 ? _photonote_KEY_OCR_TEXT : "";
        prevOCRText.push(ocrText);
    });
    const updatedProps = {};
    if ((0, _arePropsEqual.default)(prevOCRText, nextOCRTEXT) === false) {
        updatedProps[KEY_OCR_TEXT] = nextOCRTEXT;
    }
    return updatedProps;
}
function checkIRVIEWER(settings = {}, imageInfo = {}) {
    const KEY_IRVIEWER = _enums.ImageInfoKeys.IRVIEWER;
    const KEY_GLOBAL_MARKER_VISIBLE = _enums.ValidSettings.GLOBAL_MARKER_VISIBLE;
    const KEY_REFERENCE_MARKER_ID = _enums.ValidSettings.REFERENCE_MARKER_ID;
    const KEY_STATUS = _enums.ValidSettings.STATUS;
    const KEY_ASSET_TAG = _enums.ValidSettings.ASSET_TAG;
    const keys = [
        KEY_GLOBAL_MARKER_VISIBLE,
        KEY_REFERENCE_MARKER_ID,
        KEY_STATUS,
        KEY_ASSET_TAG
    ];
    const nextProps = {};
    keys.forEach((key)=>{
        if (key in settings) {
            nextProps[key] = settings[key];
        }
    });
    const irviewer = imageInfo[KEY_IRVIEWER] || {};
    if (KEY_GLOBAL_MARKER_VISIBLE in nextProps) {
        var _nextProps_KEY_GLOBAL_MARKER_VISIBLE;
        nextProps[KEY_GLOBAL_MARKER_VISIBLE] = (_nextProps_KEY_GLOBAL_MARKER_VISIBLE = nextProps[KEY_GLOBAL_MARKER_VISIBLE]) !== null && _nextProps_KEY_GLOBAL_MARKER_VISIBLE !== void 0 ? _nextProps_KEY_GLOBAL_MARKER_VISIBLE : true;
    }
    if (KEY_REFERENCE_MARKER_ID in nextProps) {
        var _nextProps_KEY_REFERENCE_MARKER_ID;
        nextProps[KEY_REFERENCE_MARKER_ID] = (_nextProps_KEY_REFERENCE_MARKER_ID = nextProps[KEY_REFERENCE_MARKER_ID]) !== null && _nextProps_KEY_REFERENCE_MARKER_ID !== void 0 ? _nextProps_KEY_REFERENCE_MARKER_ID : "";
    }
    if (KEY_STATUS in nextProps) {
        nextProps[KEY_STATUS] = (0, _utilities.checkStatusObject)(nextProps[KEY_STATUS]);
    }
    if (KEY_ASSET_TAG in nextProps) {
        var _nextProps_KEY_ASSET_TAG;
        nextProps[KEY_ASSET_TAG] = (_nextProps_KEY_ASSET_TAG = nextProps[KEY_ASSET_TAG]) !== null && _nextProps_KEY_ASSET_TAG !== void 0 ? _nextProps_KEY_ASSET_TAG : "";
    }
    const updatedProps = {};
    for(const key in nextProps){
        const value = nextProps[key];
        const isEqual = (0, _arePropsEqual.default)(irviewer[key], value);
        if (isEqual === false) updatedProps[key] = value;
    }
    return updatedProps;
}
function checkTEXTNOTES(settings = {}, imageInfo = {}) {
    const { TEXTNOTES } = _enums.ValidSettings;
    const prevNotes = imageInfo[TEXTNOTES];
    const nextNotes = settings[TEXTNOTES];
    const updatedProps = {};
    if ((0, _arePropsEqual.default)(prevNotes, nextNotes) === false) {
        updatedProps[TEXTNOTES] = nextNotes;
    }
    return updatedProps;
}
function checkThermalImageInfo(settings = {}, imageInfo = {}, frameIndex) {
    const KEY_INFO_DATA = _enums.ImageInfoKeys.THERMAL_IMAGE_INFO_DATA;
    const KEY_CAPTURED_EMISSIVITY = _enums.ValidSettings.CAPTURED_EMISSIVITY;
    const KEY_CAPTURED_TRANSMISSION = _enums.ValidSettings.CAPTURED_TRANSMISSION;
    const KEY_CAPTURED_BACKGROUND_TEMP = _enums.ValidSettings.CAPTURED_BACKGROUND_TEMP;
    const keys = [
        KEY_CAPTURED_EMISSIVITY,
        KEY_CAPTURED_TRANSMISSION,
        KEY_CAPTURED_BACKGROUND_TEMP
    ];
    const nextProps = {};
    keys.forEach((key)=>{
        if (key in settings) {
            nextProps[key] = settings[key];
        }
    });
    const info = _ImageInfo.default.getByKey(KEY_INFO_DATA, imageInfo, frameIndex);
    const updatedProps = {};
    for(const key in nextProps){
        const value = nextProps[key];
        const isEqual = (0, _arePropsEqual.default)(info[key], value);
        if (isEqual === false) updatedProps[key] = value;
    }
    return updatedProps;
}
function checkImageInfo(settings = {}, imageInfo = {}, frameIndex = 0) {
    let nextAttrs = {};
    let changedKeys = [];
    if (!imageInfo || !hasKeys(imageInfo)) return {
        nextAttrs,
        changedKeys
    };
    nextAttrs = {
        basicPresentationFlags: checkBasicPresentationFlags(settings, imageInfo, frameIndex),
        irPaletteSetup: checkIRPaletteSetup(settings, imageInfo, frameIndex),
        info: checkThermalImageInfo(settings, imageInfo, frameIndex),
        irviewer: checkIRVIEWER(settings, imageInfo),
        markers: checkMARKERS(settings, imageInfo),
        textnotes: checkTEXTNOTES(settings, imageInfo),
        photonotes: checkPHOTONOTES(settings, imageInfo)
    };
    changedKeys = getFlatKeys(nextAttrs);
    return {
        nextAttrs,
        changedKeys
    };
}
